import { useCallback, useEffect, useState } from "react";
import { Output } from "valibot";

import { useGlobalsContext } from "shared/hooks";
import { ActionSchema, ActionsSchema } from "shared/schemas";

import { fetchCommand } from "./functions";

function useScheduledActions(actions: Output<typeof ActionsSchema>) {
  const { getParentUrl, parentWindowScroll, setWidgetExpanded } =
    useGlobalsContext();
  const [scrollActions, setScrollActions] = useState<
    Output<typeof ActionsSchema>
  >([]);

  const scheduleAction = useCallback(
    (action: Output<typeof ActionSchema>) => {
      for (const command of action.commands) {
        fetchCommand(setWidgetExpanded, command);
      }
    },
    [setWidgetExpanded],
  );

  useEffect(() => {
    if (!scrollActions.length) {
      return;
    }

    const completedActions: Output<typeof ActionsSchema> = [];

    for (const scrollAction of scrollActions) {
      if (parentWindowScroll >= Number(scrollAction.scrollPercentage)) {
        for (const command of scrollAction.commands) {
          fetchCommand(setWidgetExpanded, command);
        }
        completedActions.push(scrollAction);
      }
    }

    if (completedActions.length) {
      setScrollActions(prevScrollActions =>
        prevScrollActions.filter(
          prevScrollAction => !completedActions.includes(prevScrollAction),
        ),
      );
    }
  }, [setWidgetExpanded, scrollActions, parentWindowScroll]);

  useEffect(() => {
    if (!actions.length) {
      return;
    }
    const timeouts: NodeJS.Timeout[] = [];
    const scheduleActions = async () => {
      const parentUrl = await new Promise<URL>(resolve =>
        getParentUrl(resolvedParentUrl => resolve(resolvedParentUrl)),
      );

      const newScrollActions = [];

      for (const action of actions) {
        const urlFits = action.urls.some(url => {
          const urlRegExp = new RegExp(url);
          return urlRegExp.test(parentUrl.href);
        });
        if (!urlFits) {
          continue;
        }
        if (action.timeout) {
          const timeout = setTimeout(
            () => scheduleAction(action),
            action.timeout,
          );
          timeouts.push(timeout);
        }
        if (action.scrollPercentage) {
          newScrollActions.push(action);
        }
      }

      setScrollActions(newScrollActions);
    };

    scheduleActions();

    return () => {
      for (const timeout of timeouts) {
        clearTimeout(timeout);
      }
    };
  }, [getParentUrl, scheduleAction, actions]);
}

export default useScheduledActions;
